import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import PlanFeatureList from './PlanFeatureList';
import { buildTrialFeatures } from '../utils/planFeatures';
import { usePieceEstimate } from '../hooks/usePieceEstimate';

/**
 * What the free trial includes, opened from the Active Plan card.
 *
 * The card used to assert "all premium AI features" and name none of them, so
 * the reader had to take it on trust. This is the answer to that: the same
 * ticked list the plan cards carry, rendered by the same component (see
 * PlanFeatureList) so "similar to the manage plan page" stays true rather than
 * being true only on the day it was written.
 *
 * The ROWS differ from a plan's — see buildTrialFeatures for which and why.
 *
 * Close is the only action. This is a reference panel, not a decision point:
 * the card underneath already carries the two routes onward, and putting an
 * upgrade button here would turn "what am I getting?" into a sales prompt at
 * the moment someone is simply trying to read.
 */
export default function TrialFeaturesDialog({
  open,
  onClose,
  credits,
  websiteLimit,
}) {
  const pieces = usePieceEstimate(Number(credits) > 0 ? Number(credits) : null);
  const features = buildTrialFeatures({ credits, pieces, websiteLimit });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="trial-features-title"
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
    >
      <DialogTitle id="trial-features-title" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, pb: 0.5 }}>
        Free trial
        <Typography
          component="p"
          sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: '#5A5F55', mt: 0.5 }}
        >
          See what is included.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <PlanFeatureList features={features} />
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#FFFFFF',
            borderColor: '#DDE0D8',
            color: '#5A5F55',
            '&:hover': { borderColor: '#DDE0D8', bgcolor: 'rgba(0,0,0,0.03)' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
